import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000ComboBox from "../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import U2000InputDecimal from "../../../u2000Core/components/formControls/input/U2000InputDecimal";
import { U2000FormStateEnumState } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000CheckBox from "../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import { useU2000AuthContext } from "../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU3130TimesheetWeekView } from "./useU3130TimesheetWeekView";
import {
  U2000_TIMESHEET_WEEK_DAYS,
  u2000SumHonorariumDayTotals,
  type U2000TimesheetWeekDay,
} from "../../../u2000Core/tools/U2000TimesheetHonorariumDayTotals";
import {
  u2000SumCrossTabAbsenceHoursFooter,
  u2000SumCrossTabSpecialNetHoursFooter,
} from "../../../u2000Core/tools/U2000TimesheetBankAggregates";
import { useU3130Context } from "../../U3130Context";
import { useU3130FindProjectHandlers, useU3130FindTasksForProjectHandlers } from "../../U3130UseFindProjectSubprojectHandlers";
import {
  ensureBankHoursList,
  newHonorariumClientRowId,
  type U3130FindProjectItem,
  type U3130FindSubprojectItem,
  type U3130HonorariumItem,
  type U3130TaskItem,
  type U3130Timesheet,
} from "../../U3130TimesheetModel";
import { NS } from "../../U3130";
import { U3130_READONLY_TABLE_CELL_BOX_CLASS } from "../../tools/u3130ReadonlyTableCellClass";
import {
  getHonorariumTemplateStorageKey,
  parseHonorariumTemplatePayload,
  serializeHonorariumTemplate,
  stringifyHonorariumTemplatePayload,
  templateRowsToHonorariumItems,
} from "../../tools/u3130HonorariumTemplate";
import { U3130LineProjectSubprojectRowCells } from "./U3130LineProjectSubprojectRowCells";
import U3130HonorariumReadonlyGrid from "./U3130HonorariumReadonlyGrid";
import { FaBookmark, FaCheck, FaClone, FaPlus, FaRegClock, FaTimesCircle, FaTrash } from "react-icons/fa";
import U3147DayDetailReadonlyDialog from "../../../u3147TimeTracking/components/common/U3147DayDetailReadonlyDialog";
import { useU3147WeekDaySessions } from "../../../u3147TimeTracking/shared/useU3147WeekDaySessions";
import { U3147ROLES } from "../../../u3147TimeTracking/U3147";
import { formatCurrency, type CurrencyFormatOptions } from "../../../u2000Core/tools/U2000CurrencyUtils";

const DAYS = U2000_TIMESHEET_WEEK_DAYS;

/** Même présentation que U2000InputDecimal (défauts) pour la colonne Total = somme des jours (sans état interne async). */
const HORIZONTAL_ROW_TOTAL_FORMAT: CurrencyFormatOptions = {
  currencySymbol: "",
  symbolPosition: "left",
  decimalSeparator: ".",
  thousandSeparator: " ",
  allowNegative: true,
  showNegativeInParentheses: false,
  decimalDigits: 2,
};

function formatHorizontalRowTotalHours(total: number): string {
  return formatCurrency(total, HORIZONTAL_ROW_TOTAL_FORMAT);
}

/** Heures d’un jour sur une ligne (lecture seule) — aligné sur `rowTotalHours` / `Number(x)||0`. */
function formatHonorariumDayCellHours(value: unknown): string {
  return formatCurrency(Number(value) || 0, HORIZONTAL_ROW_TOTAL_FORMAT);
}

const U3130_HOUR_COL_CLASS = "w-[5%] min-w-26";
const U3130_PROJECT_COL_CLASS = "w-40 min-w-40";
const U3130_CUSTOMER_COL_CLASS = "min-w-88";
const U3130_SUBPROJECT_COL_CLASS = "w-48 min-w-48";
const U3130_TASK_COL_CLASS = "w-[18rem] min-w-[18rem]";
const U3130_TOTAL_LABEL_COL_CLASS = "min-w-208";
/** Panneau Projet : mêmes padding / alignement pour en-têtes et pied des colonnes jour + Total. */
const U3130_PROJECT_DAY_HEADER_CELL_CLASS = `px-1 py-2.5 pr-5 text-right tabular-nums ${U3130_HOUR_COL_CLASS}`;
const U3130_PROJECT_DAY_FOOTER_CELL_CLASS = `px-1 py-2.5 pr-7 text-right tabular-nums ${U3130_HOUR_COL_CLASS}`;
const U3130_PROJECT_CROSS_FOOTER_CELL_CLASS = `px-1 py-2.5 pr-7 text-right tabular-nums text-gray-600 ${U3130_HOUR_COL_CLASS}`;

function rowTotalHours(row: U3130HonorariumItem | undefined): number {
  if (!row) return 0;
  return DAYS.reduce((acc, d) => acc + (Number(row[d]) || 0), 0);
}

/** Libellé affiché pour une tâche (initLU) : description selon la langue UI, sinon l’autre langue, puis code, puis id. */
function formatU3130TaskLabel(task: U3130TaskItem | undefined, locale: string): string {
  if (!task) return "";
  const isFr = (locale || "en").toLowerCase().startsWith("fr");
  const primary = isFr ? task.taskDescriptionFr?.trim() : task.taskDescriptionEn?.trim();
  if (primary) return primary;
  const fallback = isFr ? task.taskDescriptionEn?.trim() : task.taskDescriptionFr?.trim();
  if (fallback) return fallback;
  return task.taskCode?.trim() || task.id;
}

function emptyHonorariumRow(): U3130HonorariumItem {
  return {
    clientRowId: newHonorariumClientRowId(),
    projectId: "",
    subprojectId: "",
    taskType: "",
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
  };
}

interface U3130HonorariumTabProps {
  formStateHandlers: U2000FormStateHandlers<U3130Timesheet>;
}

interface HonorariumRowEditorProps {
  index: number;
  formStateHandlers: U2000FormStateHandlers<U3130Timesheet>;
  findProjectHandlers: ReturnType<typeof useU3130FindProjectHandlers>;
  getProjectId: () => string | undefined | null;
  /** Clé stable pour le cache des tâches (projet/sous-projet) et le payload de sauvegarde. */
  rowClientId: string;
  readonly: boolean;
  /** Désactiver la case à cocher (ex. une seule ligne visible). */
  checkboxDisabled?: boolean;
  /** Personnalisation initLU : affichage colonne sous-projet. */
  showSubProjectColumn: boolean;
  /** Afficher la colonne Sous-projet pour cette ligne (projet sélectionné includeSubProjet === true). */
  rowIncludeSubProject: boolean;
  visibleDays: readonly U2000TimesheetWeekDay[];
}

function HonorariumRowEditor({
  index,
  formStateHandlers,
  findProjectHandlers,
  getProjectId,
  rowClientId,
  readonly,
  checkboxDisabled,
  showSubProjectColumn,
  rowIncludeSubProject,
  visibleDays,
}: HonorariumRowEditorProps) {
  const { t, i18n } = useTranslation();
  const { setHonorariumTasksForRow } = useU3130Context();
  const { fetchTasks } = useU3130FindTasksForProjectHandlers();
  const [rowTasks, setRowTasks] = useState<U3130TaskItem[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const baseField = `honoraria.${index}`;

  const data = formStateHandlers.getData();
  const row = data?.honoraria?.[index];

  // Garder le total calculé en sync dans le form state pour U2000InputDecimal
  useEffect(() => {
    const currentData = formStateHandlers.getData();
    const currentRow = currentData?.honoraria?.[index];
    const total = rowTotalHours(currentRow);
    const currentTotal = (currentRow as unknown as Record<string, unknown>)?.totalHours;
    if (currentTotal !== total) {
      const list = currentData?.honoraria ?? [];
      const updated = list.map((r, i) => (i === index ? { ...r, totalHours: total } : r));
      formStateHandlers.setData({ ...currentData, honoraria: updated } as U3130Timesheet);
    }
  }, [index, formStateHandlers, row?.monday, row?.tuesday, row?.wednesday, row?.thursday, row?.friday, row?.saturday, row?.sunday]);

  const projectIdForTasks = useMemo(() => {
    const pid = row?.projectId?.trim();
    if (pid) return pid;
    return row?.project?.id?.trim() ?? "";
  }, [row?.projectId, row?.project?.id]);
  const hasProjectSelection = !!projectIdForTasks;

  const subprojectIdForTasks = useMemo(() => {
    const sid = row?.subprojectId?.trim();
    if (sid) return sid;
    const sp = (row as unknown as Record<string, unknown>)?.subproject as U3130FindSubprojectItem | undefined;
    return sp?.id?.trim() ?? "";
  }, [row]);

  const shouldUseSubprojectForTasks = showSubProjectColumn && (rowIncludeSubProject || !!subprojectIdForTasks);
  const canLoadTasks = !readonly && !!projectIdForTasks && (!shouldUseSubprojectForTasks || !!subprojectIdForTasks);

  useEffect(() => {
    if (readonly) {
      setRowTasks([]);
      return;
    }
    if (!canLoadTasks) {
      setRowTasks([]);
      if (rowClientId) {
        setHonorariumTasksForRow(rowClientId, []);
      }
      return;
    }
    let cancelled = false;
    setTasksLoading(true);
    void fetchTasks({
      projectId: projectIdForTasks,
      subprojectId: shouldUseSubprojectForTasks ? subprojectIdForTasks || null : null,
    })
      .then((list) => {
        if (cancelled) return;
        setRowTasks(list);
        if (rowClientId) {
          setHonorariumTasksForRow(rowClientId, list);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setTasksLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [readonly, canLoadTasks, projectIdForTasks, subprojectIdForTasks, shouldUseSubprojectForTasks, fetchTasks, rowClientId, setHonorariumTasksForRow]);

  const taskComboOptions = useMemo(() => {
    const tid = row?.taskType?.trim();
    if (!tid || rowTasks.some((tk) => tk.id === tid)) return rowTasks;
    const desc = row?.task?.description?.trim();
    const stub: U3130TaskItem = {
      id: tid,
      taskCode: desc,
      taskDescriptionFr: desc,
      taskDescriptionEn: desc,
    };
    return [stub, ...rowTasks];
  }, [rowTasks, row?.taskType, row?.task?.description]);

  const taskComboDisabled = tasksLoading || !canLoadTasks;

  if (readonly) {
    return (
      <tr className="border-b border-gray-100 hover:bg-gray-50/80">
        <td className="w-10 pl-2 pr-1 py-2 align-middle">
          <U2000CheckBox formStateHandlers={formStateHandlers} fieldName={`${baseField}.checked`} label="" readonly={readonly || checkboxDisabled} />
        </td>
        <U3130LineProjectSubprojectRowCells
          baseField={baseField}
          arrayField="honoraria"
          index={index}
          formStateHandlers={formStateHandlers}
          findProjectHandlers={findProjectHandlers}
          getProjectId={getProjectId}
          readonly={readonly}
          rowIncludeSubProject={rowIncludeSubProject}
          showSubProjectColumn={showSubProjectColumn}
          clearTaskFieldsOnProjectChange
          ns={NS}
        />
        <td className={`px-3 py-1.5 align-top text-left min-w-0 ${U3130_TASK_COL_CLASS}`}>
          <div className={U3130_READONLY_TABLE_CELL_BOX_CLASS}>{row?.task?.description?.trim() || row?.taskType || "—"}</div>
        </td>
        {visibleDays.map((d) => (
          <td key={d} className={`px-1 py-1.5 ${U3130_HOUR_COL_CLASS} align-top text-right`}>
            <div className={`${U3130_READONLY_TABLE_CELL_BOX_CLASS} justify-end tabular-nums`}>{formatHonorariumDayCellHours(row?.[d])}</div>
          </td>
        ))}
        <td className={`px-1 py-1.5 ${U3130_HOUR_COL_CLASS} align-top text-right`}>
          <div className={`${U3130_READONLY_TABLE_CELL_BOX_CLASS} justify-end tabular-nums`}>{formatHorizontalRowTotalHours(rowTotalHours(row))}</div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors">
      <td className="w-10 pl-2 pr-1 py-2 align-middle">
        <U2000CheckBox formStateHandlers={formStateHandlers} fieldName={`${baseField}.checked`} label="" readonly={readonly || checkboxDisabled} />
      </td>
      <U3130LineProjectSubprojectRowCells
        baseField={baseField}
        arrayField="honoraria"
        index={index}
        formStateHandlers={formStateHandlers}
        findProjectHandlers={findProjectHandlers}
        getProjectId={getProjectId}
        readonly={readonly}
        rowIncludeSubProject={rowIncludeSubProject}
        showSubProjectColumn={showSubProjectColumn}
        clearTaskFieldsOnProjectChange
        ns={NS}
      />
      {!hasProjectSelection ? (
        <>
          <td className={`px-3 py-2 align-top text-left min-w-0 ${U3130_TASK_COL_CLASS}`}>
            <div className={U3130_READONLY_TABLE_CELL_BOX_CLASS} />
          </td>
          {visibleDays.map((d) => (
            <td key={d} className={`px-1 py-1.5 align-top ${U3130_HOUR_COL_CLASS} text-right`}>
              <div className={`${U3130_READONLY_TABLE_CELL_BOX_CLASS} justify-end tabular-nums`} />
            </td>
          ))}
          <td className={`px-1 py-1.5 ${U3130_HOUR_COL_CLASS} align-top text-right`}>
            <div className={`${U3130_READONLY_TABLE_CELL_BOX_CLASS} justify-end tabular-nums`} />
          </td>
        </>
      ) : (
        <>
          <td className={`px-3 py-2 align-top text-left min-w-0 ${U3130_TASK_COL_CLASS}`}>
            <div className="text-left [&_input]:text-left [&_button]:text-left min-w-0">
              <U2000ComboBox<U3130TaskItem>
                formStateHandlers={formStateHandlers}
                fieldName={`${baseField}.taskType`}
                label=""
                data={taskComboOptions}
                keyField="id"
                descField="taskCode"
                formatLabel={(item) => formatU3130TaskLabel(item, i18n.language)}
                findItemByValue={(value, data) => data.find((d) => d.id === value) ?? null}
                getValue={(item) => item.id}
                disabled={taskComboDisabled}
                placeholder={
                  tasksLoading
                    ? t("msgHonorariumTasksLoading", { ns: NS, defaultValue: "Chargement des tâches…" })
                    : !canLoadTasks
                      ? t("placeholderTaskNeedProjectSubproject", { ns: NS, defaultValue: "Projet / sous-projet requis" })
                      : "—"
                }
              />
            </div>
          </td>
          {visibleDays.map((d) => (
            <td key={d} className={`px-1 py-1.5 align-top ${U3130_HOUR_COL_CLASS} text-right`}>
              <div className="text-right [&_input]:text-right">
                <U2000InputDecimal formStateHandlers={formStateHandlers} fieldName={`${baseField}.${d}`} label="" readonly={false} decimalDigits={2} />
              </div>
            </td>
          ))}
          <td className={`px-1 py-1.5 ${U3130_HOUR_COL_CLASS} align-top text-right`}>
            <div className={`${U3130_READONLY_TABLE_CELL_BOX_CLASS} justify-end tabular-nums`}>{formatHorizontalRowTotalHours(rowTotalHours(row))}</div>
          </td>
        </>
      )}
    </tr>
  );
}

const U3130HonorariumTab: React.FC<U3130HonorariumTabProps> = ({ formStateHandlers }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { auth, hasRole } = useU2000AuthContext();
  const { initLU } = useU3130Context();
  const showSubProjectColumn = useMemo(() => initLU.personalization?.admIncludeSubProject === "true", [initLU.personalization?.admIncludeSubProject]);
  const projectFooterLabelColSpan = showSubProjectColumn ? 4 : 3;
  const [deletePendingIndices, setDeletePendingIndices] = useState<number[] | null>(null);
  const [dayDialogDate, setDayDialogDate] = useState<string | null>(null);
  const confirmYesButtonRef = useRef<HTMLButtonElement>(null);
  const honorariumTemplateStorageKey = useMemo(
    () => (auth.isAuthenticated && auth.user.userName ? getHonorariumTemplateStorageKey(auth.user.userName, auth.user.organizationId || "noorg") : "U3130_guest_honorariumTemplate"),
    [auth.isAuthenticated, auth.user.organizationId, auth.user.userName],
  );

  const [storedTemplateLineCount, setStoredTemplateLineCount] = useState(0);

  useEffect(() => {
    const rows = parseHonorariumTemplatePayload(localStorage.getItem(honorariumTemplateStorageKey));
    setStoredTemplateLineCount(rows?.length ?? 0);
  }, [honorariumTemplateStorageKey]);

  const findProjectHandlers = useU3130FindProjectHandlers();

  const data = formStateHandlers.getData();
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ || (data != null && data.status !== "Open");

  const canViewU3147Day = hasRole(U3147ROLES.READ);
  const { showWeekend, setShowWeekend, visibleDays, dayLabelsWithDates, dayDates } = useU3130TimesheetWeekView();
  const { byDate } = useU3147WeekDaySessions(
    data?.periodDate,
    data?.employeeId,
    !!data?.periodDate && !!data?.employeeId && canViewU3147Day,
  );
  const projectTableEmptyColSpan = (showSubProjectColumn ? 5 : 4) + visibleDays.length + 1;

  const renderDayHeader = useCallback(
    (d: U2000TimesheetWeekDay) => (
      <span className="inline-flex items-center justify-end gap-0.5">
        <span>{dayLabelsWithDates?.[d] ?? t(`lbl${d.charAt(0).toUpperCase()}${d.slice(1)}`, { ns: NS })}</span>
        {dayDates?.[d] && (byDate[dayDates[d]]?.sessions?.length ?? 0) > 0 && (
          <button
            type="button"
            title={t("btnViewTimeTrackerDay", { ns: NS, defaultValue: "Voir le suivi de temps du jour" })}
            onClick={() => setDayDialogDate(dayDates[d])}
            className="ml-1 inline-flex align-middle text-gray-500 hover:text-blue-600"
          >
            <FaRegClock className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    ),
    [byDate, dayDates, dayLabelsWithDates, t],
  );

  useEffect(() => {
    const current = formStateHandlers.getData();
    const list = current?.honoraria ?? [];
    if (!list.length) return;
    let changed = false;
    const next = list.map((r) => {
      if (r.clientRowId?.trim()) return r;
      changed = true;
      return { ...r, clientRowId: newHonorariumClientRowId() };
    });
    if (changed) {
      formStateHandlers.setData({ ...current, honoraria: next } as U3130Timesheet);
    }
  }, [data?.honoraria, formStateHandlers]);

  const rows = useMemo(() => data?.honoraria ?? [], [data?.honoraria]);

  /** Préfixe les clés React pour forcer le remontage des champs décimaux au changement d’enregistrement (évite le flash de l’ancienne feuille). */
  const honorariumRowKeyPrefix = data?.id?.trim() || "draft";

  const hasCheckedRow = useMemo(() => rows.some((row) => (row as unknown as Record<string, unknown>).checked === true), [rows]);

  const handleDeleteCheckedRow = useCallback(() => {
    const list = formStateHandlers.getData()?.honoraria ?? [];
    const indices = list.map((r, i) => ((r as unknown as Record<string, unknown>).checked === true ? i : -1)).filter((i) => i >= 0);
    if (indices.length > 0) setDeletePendingIndices(indices);
  }, [formStateHandlers]);

  useEffect(() => {
    if (deletePendingIndices != null && deletePendingIndices.length > 0) {
      const t = setTimeout(() => confirmYesButtonRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [deletePendingIndices]);

  const footerTotals = useMemo(() => u2000SumHonorariumDayTotals(rows), [rows]);
  const bankHoursList = useMemo(() => ensureBankHoursList(data?.bankHoursList), [data?.bankHoursList]);
  const absenceCrossFooterTotals = useMemo(() => u2000SumCrossTabAbsenceHoursFooter(bankHoursList), [bankHoursList]);
  const specialNetCrossFooterTotals = useMemo(() => u2000SumCrossTabSpecialNetHoursFooter(bankHoursList), [bankHoursList]);
  const workedCrossFooterTotals = useMemo(() => {
    const daySums: Record<string, number> = {};
    for (const d of DAYS) {
      daySums[d] =
        (footerTotals.daySums[d] ?? 0) + (absenceCrossFooterTotals.daySums[d] ?? 0) + (specialNetCrossFooterTotals.daySums[d] ?? 0);
    }
    return {
      daySums,
      grandTotal: footerTotals.grandTotal + absenceCrossFooterTotals.grandTotal + specialNetCrossFooterTotals.grandTotal,
    };
  }, [absenceCrossFooterTotals, footerTotals, specialNetCrossFooterTotals]);

  useEffect(() => {
    const current = formStateHandlers.getData();
    if (current == null) return;
    const stored = current.honorariaFooterTotals;
    const needUpdate = !stored || DAYS.some((d) => (stored[d] ?? 0) !== footerTotals.daySums[d]) || (stored.grandTotal ?? 0) !== footerTotals.grandTotal;
    if (needUpdate) {
      formStateHandlers.setData({
        ...current,
        honorariaFooterTotals: {
          ...footerTotals.daySums,
          grandTotal: footerTotals.grandTotal,
        },
      });
    }
  }, [footerTotals, formStateHandlers]);

  const handleAddRow = useCallback(() => {
    const current = formStateHandlers.getData();
    const list = current?.honoraria ?? [];
    formStateHandlers.setData({
      ...current,
      honoraria: [...list, emptyHonorariumRow()],
    } as U3130Timesheet);
  }, [formStateHandlers]);

  const handleSaveHonorariumTemplate = useCallback(() => {
    const list = formStateHandlers.getData()?.honoraria ?? [];
    const templateRows = serializeHonorariumTemplate(list);
    if (templateRows.length === 0) {
      toast.error(t("msgHonorariumTemplateEmpty", { ns: NS, defaultValue: "Aucune ligne avec projet à enregistrer comme modèle." }));
      return;
    }
    try {
      localStorage.setItem(honorariumTemplateStorageKey, stringifyHonorariumTemplatePayload(templateRows));
      setStoredTemplateLineCount(templateRows.length);
      toast.success(t("msgHonorariumTemplateSaved", { ns: NS, defaultValue: "Modèle d’honoraires enregistré sur cet appareil ({{count}} ligne(s)).", count: templateRows.length }));
    } catch {
      toast.error(t("msgHonorariumTemplateSaveFailed", { ns: NS, defaultValue: "Impossible d’enregistrer le modèle (stockage local)." }));
    }
  }, [formStateHandlers, honorariumTemplateStorageKey, t, toast]);

  const handleApplyHonorariumTemplate = useCallback(() => {
    const rows = parseHonorariumTemplatePayload(localStorage.getItem(honorariumTemplateStorageKey));
    if (rows == null || rows.length === 0) {
      toast.error(t("msgHonorariumTemplateNone", { ns: NS, defaultValue: "Aucun modèle enregistré." }));
      return;
    }
    const current = formStateHandlers.getData();
    const existing = current?.honoraria ?? [];
    const appended = templateRowsToHonorariumItems(rows);
    formStateHandlers.setData({
      ...current,
      honoraria: [...existing, ...appended],
    } as U3130Timesheet);
    toast.success(t("msgHonorariumTemplateApplied", { ns: NS, defaultValue: "{{count}} ligne(s) du modèle ajoutée(s).", count: appended.length }));
  }, [formStateHandlers, honorariumTemplateStorageKey, t, toast]);

  const confirmDeleteRow = useCallback(() => {
    if (deletePendingIndices == null || deletePendingIndices.length === 0) return;
    const toRemove = new Set(deletePendingIndices);
    const current = formStateHandlers.getData();
    const list = current?.honoraria ?? [];
    const next = list.filter((_, i) => !toRemove.has(i));
    const nextData = {
      ...current,
      honoraria: next,
    } as U3130Timesheet;
    formStateHandlers.setData(nextData);
    const validateForm = formStateHandlers.getBusinessHandlers()?.validateForm;
    formStateHandlers.setErrors(validateForm ? validateForm(nextData) : {});
    setDeletePendingIndices(null);
  }, [deletePendingIndices, formStateHandlers]);

  const getProjectIdForRow = useCallback(
    (index: number) => {
      const r = formStateHandlers.getData()?.honoraria?.[index];
      const proj = r && (r as unknown as Record<string, unknown>).project;
      const pid = proj && typeof proj === "object" && "id" in proj ? String((proj as { id?: string }).id ?? "") : "";
      return r?.projectId?.trim() || pid || undefined;
    },
    [formStateHandlers],
  );

  const getRowIncludeSubProject = useCallback(
    (index: number): boolean => {
      const r = formStateHandlers.getData()?.honoraria?.[index];
      const project = (r as unknown as Record<string, unknown>)?.project as U3130FindProjectItem | undefined;
      const v = project?.includeSubProjet;
      return v === true || (typeof v === "string" && v === "true");
    },
    [formStateHandlers],
  );

  const dayDetailDialog = (
    <U3147DayDetailReadonlyDialog
      isOpen={dayDialogDate != null}
      onClose={() => setDayDialogDate(null)}
      date={dayDialogDate ?? ""}
      dayResult={dayDialogDate ? (byDate[dayDialogDate] ?? null) : null}
      showSubproject={showSubProjectColumn}
    />
  );

  if (readonly) {
    // Hauteur naturelle (comme U3129) : le tabpanel U2000 défile, pas de scroll imbriqué dans la grille.
    return (
      <div className="flex min-w-0 flex-col gap-2 rounded-lg border border-gray-200 bg-muted/10 px-4 pb-2 pt-3">
        <div className="flex w-full shrink-0 justify-end">
          <label className="flex min-h-9 shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700">
            <input type="checkbox" className="h-4 w-4 shrink-0" checked={showWeekend} onChange={(e) => setShowWeekend(e.target.checked)} />
            {t("lblShowWeekendDays", { ns: NS, defaultValue: "Afficher samedi et dimanche" })}
          </label>
        </div>
        <U3130HonorariumReadonlyGrid
          key={honorariumRowKeyPrefix}
          rows={rows}
          formStateHandlers={formStateHandlers}
          visibleDays={visibleDays}
          showSubProjectColumn={showSubProjectColumn}
          renderDayHeader={renderDayHeader}
          formatHours={formatHonorariumDayCellHours}
          projectTotals={footerTotals}
          absenceTotals={absenceCrossFooterTotals}
          specialNetTotals={specialNetCrossFooterTotals}
          workedTotals={workedCrossFooterTotals}
        />
        {dayDetailDialog}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-muted/10 px-4 pb-2 pt-4">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-hidden">
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col gap-4">
          {deletePendingIndices != null && deletePendingIndices.length > 0 && (
            <div
              className="absolute inset-0 z-100 flex items-center justify-center rounded-lg bg-black/30 backdrop-blur-[1px]"
              aria-modal="true"
              role="alertdialog"
              aria-labelledby="confirm-delete-honorarium-title"
            >
              <div className="relative z-101 mx-4 w-full max-w-md rounded-xl border border-gray-200 bg-white p-5 shadow-lg">
                <h3 id="confirm-delete-honorarium-title" className="text-lg font-semibold text-gray-900">
                  {deletePendingIndices.length === 1
                    ? t("msgConfirmDeleteHonorariumRow", { ns: NS, defaultValue: "Supprimer cette ligne ?" })
                    : t("msgConfirmDeleteHonorariumRowsBulk", {
                        ns: NS,
                        count: deletePendingIndices.length,
                        defaultValue: "Supprimer {{count}} lignes d’honoraires ?",
                      })}
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  {deletePendingIndices.length === 1
                    ? t("msgConfirmDeleteHonorariumRowDetail", { ns: NS, defaultValue: "Voulez-vous supprimer cette entrée de la liste des honoraires ?" })
                    : t("msgConfirmDeleteHonorariumRowsBulkDetail", {
                        ns: NS,
                        count: deletePendingIndices.length,
                        defaultValue: "Les {{count}} lignes cochées seront retirées de la liste des honoraires.",
                      })}
                </p>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setDeletePendingIndices(null)}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <FaTimesCircle className="h-4 w-4" />
                    {t("dlgLblNo", { ns: "common", defaultValue: "Non" })}
                  </button>
                  <button
                    ref={confirmYesButtonRef}
                    type="button"
                    onClick={confirmDeleteRow}
                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                  >
                    <FaCheck className="h-4 w-4" />
                    {t("dlgLblYes", { ns: "common", defaultValue: "Oui" })}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mt-1 flex min-w-0 shrink-0 flex-col gap-3">
            {!readonly && (
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <U2000ActionBtn label={t("btnAdd", { ns: NS })} icon={FaPlus} handleClick={handleAddRow} />
                <U2000ActionBtn
                  label={t("btnDelete", { ns: NS })}
                  icon={FaTrash}
                  handleClick={handleDeleteCheckedRow}
                  disabled={!hasCheckedRow}
                  className="bg-red-600! hover:bg-red-700! focus:ring-red-500! active:bg-red-800! disabled:bg-gray-400!"
                />
                <U2000ActionBtn
                  label={t("btnSaveHonorariumTemplate", { ns: NS, defaultValue: "Enregistrer le modèle" })}
                  icon={FaBookmark}
                  handleClick={handleSaveHonorariumTemplate}
                  className="bg-emerald-600! hover:bg-emerald-700! focus:ring-emerald-500! active:bg-emerald-800!"
                />
                <U2000ActionBtn
                  label={t("btnApplyHonorariumTemplate", { ns: NS, defaultValue: "Appliquer le modèle" })}
                  icon={FaClone}
                  handleClick={handleApplyHonorariumTemplate}
                  disabled={storedTemplateLineCount === 0}
                  className="bg-white! text-slate-800! ring-1! ring-slate-300! hover:bg-slate-50! focus:ring-slate-400! disabled:bg-gray-100! disabled:text-gray-400!"
                />
              </div>
            )}

            <div className="flex min-w-0 shrink-0 justify-end">
              <label className="flex min-h-0 shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700">
                <input type="checkbox" className="h-4 w-4 shrink-0" checked={showWeekend} onChange={(e) => setShowWeekend(e.target.checked)} />
                {t("lblShowWeekendDays", { ns: NS, defaultValue: "Afficher samedi et dimanche" })}
              </label>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200">
                <div className="min-h-0 min-w-0 flex-1 overflow-auto [&_input]:h-[46px] [&_input]:py-2.5 [&_button]:h-[46px] [&_button]:py-2.5">
                  <table className="min-w-full text-sm tabular-nums">
                    <thead className="sticky top-0 z-10 border-b border-gray-200 bg-muted">
                      <tr>
                        <th className="w-10 pl-2 pr-1 py-2.5" />
                        <th className={`pl-3 pr-3 py-2.5 text-left font-medium text-gray-700 ${U3130_PROJECT_COL_CLASS}`}>{t("lblProject", { ns: NS, defaultValue: "Projet" })}</th>
                        <th className={`pl-7 pr-3 py-2.5 text-left font-medium text-gray-700 ${U3130_CUSTOMER_COL_CLASS}`}>{t("lblCustomer", { ns: NS, defaultValue: "Client" })}</th>
                        {showSubProjectColumn ? (
                          <th className={`pl-7 pr-3 py-2.5 text-left font-medium text-gray-700 ${U3130_SUBPROJECT_COL_CLASS}`}>{t("lblSubProject", { ns: NS, defaultValue: "Sous-projet" })}</th>
                        ) : null}
                        <th className={`pl-7 pr-3 py-2.5 text-left font-medium text-gray-700 ${U3130_TASK_COL_CLASS}`}>{t("lblTask", { ns: NS, defaultValue: "Tâche" })}</th>
                        {visibleDays.map((d) => (
                          <th key={d} className={`${U3130_PROJECT_DAY_HEADER_CELL_CLASS} font-medium text-gray-700`}>
                            {renderDayHeader(d)}
                          </th>
                        ))}
                        <th className={`${U3130_PROJECT_DAY_HEADER_CELL_CLASS} font-medium text-gray-700`}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={projectTableEmptyColSpan} className="px-4 py-8 text-center text-sm text-gray-400">
                            {t("msgNoHonorariumProjectLines", { ns: NS, defaultValue: "Aucun projet pour cette période." })}
                          </td>
                        </tr>
                      ) : (
                        rows.map((row, idx) => (
                          <HonorariumRowEditor
                            key={`${honorariumRowKeyPrefix}-${row.clientRowId ?? `idx-${idx}`}`}
                            index={idx}
                            formStateHandlers={formStateHandlers}
                            findProjectHandlers={findProjectHandlers}
                            getProjectId={() => getProjectIdForRow(idx)}
                            rowClientId={row.clientRowId?.trim() ?? ""}
                            readonly={readonly}
                            showSubProjectColumn={showSubProjectColumn}
                            rowIncludeSubProject={getRowIncludeSubProject(idx)}
                            visibleDays={visibleDays}
                          />
                        ))
                      )}
                    </tbody>
                    <tfoot className="border-t-2 border-gray-200 bg-muted shadow-[0_-4px_6px_-2px_rgba(0,0,0,0.06)]">
                      <tr aria-label={t("lblTotalProjectHours", { ns: NS, defaultValue: "Total projet" })}>
                        <td className="w-10 pl-2 pr-1 py-2.5" />
                        <td className={`pl-3 pr-3 py-2.5 text-left font-semibold text-gray-800 ${U3130_TOTAL_LABEL_COL_CLASS}`} colSpan={projectFooterLabelColSpan}>
                          {t("lblTotalProjectHours", { ns: NS, defaultValue: "Total projet" })}
                        </td>
                        {visibleDays.map((d) => (
                          <td key={d} className={`${U3130_PROJECT_DAY_FOOTER_CELL_CLASS} font-semibold text-gray-800`}>
                            {(footerTotals.daySums[d] ?? 0).toFixed(2)}
                          </td>
                        ))}
                        <td className={`${U3130_PROJECT_DAY_FOOTER_CELL_CLASS} font-bold text-gray-900`}>{footerTotals.grandTotal.toFixed(2)}</td>
                      </tr>
                      <tr aria-label={t("lblCrossTotalAbsenceHours", { ns: NS, defaultValue: "Total absence" })}>
                        <td className="w-10 pl-2 pr-1 py-2" />
                        <td className={`pl-3 pr-3 py-2 text-left text-sm font-medium text-gray-600 ${U3130_TOTAL_LABEL_COL_CLASS}`} colSpan={projectFooterLabelColSpan}>
                          {t("lblCrossTotalAbsenceHours", { ns: NS, defaultValue: "Total absence" })}
                        </td>
                        {visibleDays.map((d) => (
                          <td key={d} className={U3130_PROJECT_CROSS_FOOTER_CELL_CLASS}>
                            {(absenceCrossFooterTotals.daySums[d] ?? 0).toFixed(2)}
                          </td>
                        ))}
                        <td className={`${U3130_PROJECT_CROSS_FOOTER_CELL_CLASS} font-semibold`}>{absenceCrossFooterTotals.grandTotal.toFixed(2)}</td>
                      </tr>
                      <tr aria-label={t("lblCrossTotalSpecialBankNetHours", { ns: NS, defaultValue: "Total heures accumulées (net)" })}>
                        <td className="w-10 pl-2 pr-1 py-2" />
                        <td className={`pl-3 pr-3 py-2 text-left text-sm font-medium text-gray-600 ${U3130_TOTAL_LABEL_COL_CLASS}`} colSpan={projectFooterLabelColSpan}>
                          {t("lblCrossTotalSpecialBankNetHours", { ns: NS, defaultValue: "Total heures accumulées (net)" })}
                        </td>
                        {visibleDays.map((d) => (
                          <td key={d} className={U3130_PROJECT_CROSS_FOOTER_CELL_CLASS}>
                            {(specialNetCrossFooterTotals.daySums[d] ?? 0).toFixed(2)}
                          </td>
                        ))}
                        <td className={`${U3130_PROJECT_CROSS_FOOTER_CELL_CLASS} font-semibold`}>{specialNetCrossFooterTotals.grandTotal.toFixed(2)}</td>
                      </tr>
                      <tr aria-label={t("lblHeaderTotalHonorariumHours", { ns: NS, defaultValue: "Total honoraires" })}>
                        <td className="w-10 pl-2 pr-1 py-2" />
                        <td className={`pl-3 pr-3 py-2 text-left text-sm font-semibold text-gray-800 ${U3130_TOTAL_LABEL_COL_CLASS}`} colSpan={projectFooterLabelColSpan}>
                          {t("lblHeaderTotalHonorariumHours", { ns: NS, defaultValue: "Total honoraires" })}
                        </td>
                        {visibleDays.map((d) => (
                          <td key={d} className={`${U3130_PROJECT_DAY_FOOTER_CELL_CLASS} font-semibold text-gray-800`}>
                            {(workedCrossFooterTotals.daySums[d] ?? 0).toFixed(2)}
                          </td>
                        ))}
                        <td className={`${U3130_PROJECT_DAY_FOOTER_CELL_CLASS} font-bold text-gray-900`}>{workedCrossFooterTotals.grandTotal.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
          </div>
        </div>
      </div>
      {dayDetailDialog}
    </div>
  );
};

export default U3130HonorariumTab;
